import { api } from "./client";

const NFL = "/games/nfl";
const SV = "/games/nfl-survivor";

// ── Shared NFL data ──────────────────────────────────────────────────────
export async function getNflCurrentSeason() {
  const resp = await api.get(`${NFL}/seasons/current`);
  return resp.data;
}

export async function getNflTeams() {
  const resp = await api.get(`${NFL}/teams/`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function getNflCurrentWeek(seasonId) {
  const resp = await api.get(`${NFL}/seasons/${seasonId}/current-week`);
  return resp.data;
}

export async function getNflWeekGames(seasonId, week) {
  const resp = await api.get(`${NFL}/seasons/${seasonId}/weeks/${week}/games`);
  return resp.data;
}

export async function getNflMatchupHistory(teamA, teamB, seasons = 5) {
  const resp = await api.get(`${NFL}/matchups`, {
    params: { team_a: teamA, team_b: teamB, seasons },
  });
  return resp.data;
}

export async function getNflStandings(seasonId) {
  const resp = await api.get(`${NFL}/seasons/${seasonId}/standings`);
  return resp.data;
}

// ── Pools ────────────────────────────────────────────────────────────────
export async function getMySurvivorPools(seasonId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/mine`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function getAllSurvivorPools(seasonId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function createSurvivorPool(seasonId, { name, pool_depth }) {
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/`, { name, pool_depth });
  return resp.data;
}

export async function joinSurvivorPool(seasonId, poolId) {
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/${poolId}/join`);
  return resp.data;
}

export async function getSurvivorPool(seasonId, poolId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}`);
  return resp.data;
}

export async function updateSurvivorPool(seasonId, poolId, body) {
  const resp = await api.patch(`${SV}/seasons/${seasonId}/pools/${poolId}`, body);
  return resp.data;
}

export async function getSurvivorPayouts(seasonId, poolId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/payouts/`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function saveSurvivorPayouts(seasonId, poolId, payouts) {
  const resp = await api.put(`${SV}/seasons/${seasonId}/pools/${poolId}/payouts/`, { payouts });
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function sendSurvivorInvites(seasonId, poolId, emails, message, subject, mode) {
  // A mailshot to a full pool takes longer than the 10s default allows.
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/${poolId}/invite`, {
    emails,
    message,
    subject,
    mode,
  }, { timeout: 120000 });
  return resp.data;
}

export async function sendSurvivorRecapTest(seasonId, poolId) {
  const resp = await api.post(
    `${SV}/seasons/${seasonId}/pools/${poolId}/recap-test`,
    null,
    { timeout: 30000 }
  );
  return resp.data;
}

// ── Entries ──────────────────────────────────────────────────────────────
export async function getMyEntries(seasonId, poolId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/entries/mine`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function createEntry(seasonId, poolId, label) {
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/${poolId}/entries/`, { label: label || null });
  return resp.data;
}

export async function getBuybackStatus(seasonId, poolId, entryId, week) {
  const resp = await api.get(
    `${SV}/seasons/${seasonId}/pools/${poolId}/entries/${entryId}/buyback-status`,
    { params: { week } }
  );
  return resp.data;
}

export async function buyBackEntry(seasonId, poolId, entryId, week) {
  const resp = await api.post(
    `${SV}/seasons/${seasonId}/pools/${poolId}/entries/${entryId}/buyback`,
    null,
    { params: { week } }
  );
  return resp.data;
}

// ── Picks ────────────────────────────────────────────────────────────────
export async function getMySurvivorPicks(seasonId, poolId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/picks/mine`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function getAvailableTeams(seasonId, poolId, entryId, week) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/picks/available`, {
    params: { entry_id: entryId, week },
  });
  return resp.data;
}

export async function makeSurvivorPick(seasonId, poolId, { entry_id, game_id, team_id }) {
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/${poolId}/picks/`, {
    entry_id,
    game_id,
    team_id,
  });
  return resp.data;
}

// ── Grid / results ───────────────────────────────────────────────────────
export async function getSurvivorGrid(seasonId, poolId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/grid`);
  return resp.data;
}

export async function getWeekResults(seasonId, poolId, week) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/weeks/${week}/results`);
  return resp.data;
}

export async function getTeamUsage(seasonId, poolId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/team-usage`);
  return resp.data;
}

// ── Smack board (arg order matches the shared SmackBoard component) ──────
export async function getSvSmackMessages(poolId, seasonId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/smack/`);
  return resp.data;
}

export async function postSvSmackMessage(poolId, body, seasonId) {
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/${poolId}/smack/`, { body });
  return resp.data;
}

export async function getSvSmackReplies(poolId, messageId, seasonId) {
  const resp = await api.get(`${SV}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/replies`);
  return resp.data;
}

export async function postSvSmackReply(poolId, messageId, body, seasonId) {
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/replies`, { body });
  return resp.data;
}

export async function reactSvSmack(poolId, messageId, reaction, seasonId) {
  const resp = await api.post(`${SV}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/react`, { reaction });
  return resp.data;
}

export async function deleteSvSmackMessage(poolId, messageId, seasonId) {
  const resp = await api.delete(`${SV}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}`);
  return resp.data;
}

export async function updateSurvivorMember(seasonId, poolId, userId, body) {
  const resp = await api.patch(`${SV}/seasons/${seasonId}/pools/${poolId}/members/${userId}`, body);
  return resp.data;
}

export async function removeSurvivorMember(seasonId, poolId, userId) {
  const resp = await api.delete(`${SV}/seasons/${seasonId}/pools/${poolId}/members/${userId}`);
  return resp.data;
}
